import { ChangeDetectionStrategy, Component, Injector, input, OnInit, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, combineLatest, map } from 'rxjs';
import { Channel } from 'src/app/dto';
import { ApplicationService } from 'src/app/services/application.service';
import { BaseMessagingService } from 'src/app/services/base-messaging.service';
import { MessageboardService } from 'src/app/services/messageboard.service';
import { AuthenticatedPageComponent } from '../../base.component';
import { MainpageComponent } from '../../mainpage/mainpage.component';

// Everything a page render/paginate needs, computed together in one pipeline pass so it can
// never observe a mix of one page's channel slice with another page's next/prev flags.
interface MessageBoardPageState {
    channels: Channel[];
    page: number;
    nextPageExists: boolean;
    prevPageExists: boolean;
}

const DEFAULT_PAGE_STATE: MessageBoardPageState = { channels: [], page: 1, nextPageExists: false, prevPageExists: false };

@Component({
    selector: 'app-message-board-index',
    templateUrl: './message-board-index.component.html',
    styleUrls: ['./message-board-index.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessageBoardIndexComponent extends AuthenticatedPageComponent implements OnInit {

    public static route = 'boards';
    private static TOPICS_PER_PAGE = 10;

    // Aliased to the route param name ('page') while keeping a distinct property name, since
    // page is derived state (see pageState below) rather than something this input() could be
    // directly.
    pageParam = input<string>(undefined, { alias: 'page' });
    // Reshaped into a {page} object (matching the old route.params.subscribe access pattern
    // below: channelArrAndRouteParams[1].page) so the surrounding combineLatest/derivation logic -
    // including the pagination math - is untouched by the route.params -> input() swap.
    private pageParams$ = toObservable(this.pageParam).pipe(map(page => ({ page })));

    private pageState$: Observable<MessageBoardPageState>;
    // Bundles page/nextPageExists/prevPageExists/channels into one signal rather than four
    // separately-written fields: all four are computed from the same combineLatest emission and
    // must stay in sync with each other, which plain fields set inside the pipe's map() callback
    // can't guarantee under OnPush (the write is invisible to change detection) and separate
    // signals could still momentarily disagree with each other across two writes.
    protected pageState: Signal<MessageBoardPageState>;

    constructor(private messageBoardService: MessageboardService,
        private coreAppService: ApplicationService, router: Router, private injector: Injector) {
        super(router, coreAppService.authService, MainpageComponent.route, coreAppService.uiStateService);
    }

    ngOnInit(): void {
        this.pageState$ = combineLatest([this.messageBoardService.getPublicChannels(), this.pageParams$])
            .pipe(
                map((channelArrAndRouteParams) => {
                    const page = Number.parseInt(channelArrAndRouteParams[1].page ?? '1');
                    const startIndex = ((page - 1) * MessageBoardIndexComponent.TOPICS_PER_PAGE);
                    const endIndex = (page * MessageBoardIndexComponent.TOPICS_PER_PAGE) - 1;

                    var allCachedChannels = BaseMessagingService.sortChannelsByMessageRecency([...channelArrAndRouteParams[0].values()]
                        .map(channelSignal => channelSignal())
                        .filter(channel => channel.IsPublic));

                    if (allCachedChannels.length < (page * MessageBoardIndexComponent.TOPICS_PER_PAGE)) {
                        let lastChannel = allCachedChannels.length > 0 ? allCachedChannels[allCachedChannels.length - 1] : null;
                        let lastChannelMessage = lastChannel && lastChannel.Messages.length > 0 ? lastChannel.Messages[lastChannel.Messages.length - 1] : null
                        this.messageBoardService.retrievePublicChannels(lastChannelMessage?.SendDate).subscribe();
                    }

                    if (page === 1) {
                        // double-tap retrieve on purpose
                        this.messageBoardService.retrievePublicChannels(null).subscribe();
                    }

                    return {
                        channels: allCachedChannels.slice(startIndex, endIndex + 1),
                        page,
                        nextPageExists: allCachedChannels.length > (page * MessageBoardIndexComponent.TOPICS_PER_PAGE),
                        prevPageExists: page !== 1
                    };
                }
                ));
        // Called from ngOnInit, not a field initializer/constructor, so this needs an injector
        // passed explicitly rather than relying on an ambient injection context.
        this.pageState = toSignal(this.pageState$, { injector: this.injector, initialValue: DEFAULT_PAGE_STATE });
    }

    nextPage() {
        this.router.navigate([MessageBoardIndexComponent.route, this.pageState().page + 1]);
    }

    prevPage() {
        this.router.navigate([MessageBoardIndexComponent.route, this.pageState().page - 1]);
    }
}
